import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ShieldCheck, RotateCcw, Trophy, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { ACTIVITY_CATEGORIES, ACTIVITY_INDEX, ACTIVITY_TARGET_PER_WEEK } from "../data/activities";

/** Progress ring + instruction header. */
function ProgressCard({ accent, count, remaining, pct, stepNum }) {
  const headline =
    count === 0
      ? `Log your first activity for Week ${stepNum}.`
      : count >= ACTIVITY_TARGET_PER_WEEK
        ? `${count} logged — Week ${stepNum} unlocked. Keep going.`
        : remaining === 1
          ? "One more to unlock the next week."
          : `${remaining} more to unlock Week ${Math.min(stepNum + 1, 4)}.`;
  return (
    <div
      className="relative overflow-hidden rounded-3xl border p-5"
      style={{
        borderColor: `${accent}55`,
        background: `linear-gradient(160deg, ${accent}1A 0%, rgba(10,21,48,0.85) 70%)`,
        boxShadow: `0 16px 40px ${accent}26`,
      }}
      data-testid="activity-progress-card"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl" style={{ background: `radial-gradient(circle, ${accent}55, transparent 70%)` }} />
      <div className="relative flex items-center gap-4">
        <div className="relative w-16 h-16 shrink-0">
          <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
            <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
            <motion.circle
              cx="32" cy="32" r="28" fill="none"
              stroke={accent}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 28}`}
              initial={false}
              animate={{ strokeDashoffset: 2 * Math.PI * 28 * (1 - pct / 100) }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              style={{ filter: `drop-shadow(0 0 8px ${accent}88)` }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="font-display text-xl font-medium text-white leading-none">{count}</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="bps-eyebrow" style={{ color: accent }}>This week&apos;s activities</div>
          <div className="font-display text-base text-white mt-0.5 leading-snug">{headline}</div>
          <div className="text-[11px] text-[#7C8FA8] mt-1 leading-relaxed inline-flex items-center gap-1.5">
            <ShieldCheck size={11} style={{ color: accent }} /> Wear the Back Pain Transformer for each one.
          </div>
        </div>
      </div>
    </div>
  );
}

/** Recent completions list with per-row undo. */
function RecentList({ completions, onUndo }) {
  if (completions.length === 0) return null;
  return (
    <div className="bps-card p-4" data-testid="activity-recent">
      <div className="bps-eyebrow mb-2 text-[#7C8FA8]">Recent</div>
      <div className="space-y-2">
        {completions.slice(0, 5).map((c) => {
          const meta = ACTIVITY_INDEX[c.activity_id] || { label: c.activity_label || c.activity_id };
          const Icon = meta.icon;
          return (
            <div key={c.id} className="flex items-center gap-3 text-sm" data-testid={`activity-recent-${c.id}`}>
              <div className="w-7 h-7 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center shrink-0">
                <Check size={13} className="text-[#10B981]" strokeWidth={2.4} />
              </div>
              {Icon && <Icon size={14} className="text-[#9AAEC6] shrink-0" strokeWidth={1.6} />}
              <span className="text-white/90 flex-1 truncate">{meta.label || c.activity_label}</span>
              {!c.__optimistic && (
                <button
                  type="button"
                  onClick={() => onUndo(c.id)}
                  className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] hover:text-white inline-flex items-center gap-1"
                  data-testid={`activity-undo-${c.id}`}
                  aria-label="Undo"
                >
                  <RotateCcw size={10} /> Undo
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A single selectable activity row inside a category. */
function ActivityItem({ activity, accent, doneCount, isPending, onLog }) {
  const Icon = activity.icon;
  return (
    <li>
      <button
        type="button"
        onClick={onLog}
        disabled={isPending}
        data-testid={`activity-${activity.id}`}
        className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-left hover:bg-white/[0.04] transition-colors disabled:opacity-50"
      >
        <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-center shrink-0">
          {Icon && <Icon size={15} strokeWidth={1.6} style={{ color: accent }} />}
        </div>
        <span className="flex-1 text-sm text-white/90 leading-snug">{activity.label}</span>
        {doneCount > 0 && (
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full" style={{ background: `${accent}26`, color: accent, border: `1px solid ${accent}66` }}>
            ×{doneCount}
          </span>
        )}
        {isPending
          ? <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          : <ChevronRight size={14} className="text-[#7C8FA8]" />}
      </button>
    </li>
  );
}

/** Collapsible category section. */
function CategorySection({ cat, isOpen, onToggle, perActivityCount, pendingId, onLog }) {
  return (
    <div className="bps-card overflow-hidden" data-testid={`activity-cat-${cat.id}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`activity-cat-toggle-${cat.id}`}
        className="w-full px-4 py-3.5 flex items-center gap-3 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-lg shrink-0" style={{ background: `${cat.accent}1A`, border: `1px solid ${cat.accent}55` }}>
          <span aria-hidden>{cat.emoji}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-white leading-snug">{cat.label}</div>
          <div className="text-[11px] text-[#7C8FA8] mt-0.5 truncate">{cat.desc}</div>
        </div>
        <ChevronDown size={18} className={`text-[#9AAEC6] transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden border-t border-white/5"
          >
            <ul className="p-2">
              {cat.activities.map((a) => (
                <ActivityItem
                  key={a.id}
                  activity={a}
                  accent={cat.accent}
                  doneCount={perActivityCount[a.id] || 0}
                  isPending={pendingId === a.id}
                  onLog={() => onLog({ ...a, category: cat.id })}
                />
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Full-screen "seven complete" celebration overlay. */
function Celebration({ show, accent, stepNum }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#03101F]/85 backdrop-blur-md"
          data-testid="activity-week-celebration"
        >
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center px-6"
          >
            <div className="relative inline-flex items-center justify-center w-24 h-24 rounded-full mb-4" style={{ background: `radial-gradient(circle at 30% 30%, ${accent}55, ${accent}10 70%)`, border: `1px solid ${accent}80`, boxShadow: `0 0 32px ${accent}66` }}>
              <Trophy size={36} style={{ color: accent }} strokeWidth={1.8} />
            </div>
            <div className="bps-eyebrow" style={{ color: accent }}>Seven complete</div>
            <h2 className="font-display text-3xl font-light tracking-tighter mt-2 text-white">Week {stepNum} done.</h2>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * The interactive activity picker that powers Practice 5 (and the home mini-card).
 * Loads completions for `stepNum`, lets the user log/undo activities (repeats
 * encouraged), and auto-unlocks the week at ACTIVITY_TARGET_PER_WEEK (7).
 */
export default function ActivityPicker({ stepNum, accent = "#22D3EE", onComplete, compact = false }) {
  const [completions, setCompletions] = useState([]);
  const [openCat, setOpenCat] = useState(ACTIVITY_CATEGORIES[0].id);
  const [pendingId, setPendingId] = useState(null);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/activity-completions").then((r) => {
      if (cancelled) return;
      setCompletions((r.data || []).filter((c) => c.step === stepNum));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [stepNum]);

  // Dedupe by id so an optimistic/GET race never produces duplicate React
  // keys, a doubled "recent" row, or an inflated progress count.
  const recentCompletions = useMemo(() => {
    const seen = new Map();
    for (const c of completions) if (!seen.has(c.id)) seen.set(c.id, c);
    return Array.from(seen.values());
  }, [completions]);

  const count = recentCompletions.length;
  const remaining = Math.max(0, ACTIVITY_TARGET_PER_WEEK - count);
  const pct = Math.min(100, (count / ACTIVITY_TARGET_PER_WEEK) * 100);
  const perActivityCount = recentCompletions.reduce((acc, c) => {
    acc[c.activity_id] = (acc[c.activity_id] || 0) + 1;
    return acc;
  }, {});

  async function logActivity(activity) {
    if (pendingId) return;
    setPendingId(activity.id);
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      step: stepNum,
      activity_id: activity.id,
      activity_label: activity.label,
      category: activity.category,
      created_at: new Date().toISOString(),
      __optimistic: true,
    };
    setCompletions((prev) => [optimistic, ...prev]);
    try {
      const { data } = await api.post("/activity-completions", {
        step: stepNum,
        activity_id: activity.id,
        activity_label: activity.label,
        category: activity.category,
      });
      setCompletions((prev) => prev.map((c) => (c.id === tempId ? { ...data, __optimistic: false } : c)));
      if (data.all_seven_complete) {
        setCelebrate(true);
        setTimeout(() => {
          setCelebrate(false);
          onComplete?.();
        }, 1600);
      }
    } catch (e) {
      setCompletions((prev) => prev.filter((c) => c.id !== tempId));
    } finally {
      setPendingId(null);
    }
  }

  async function undoCompletion(completionId) {
    // Don't try to delete a not-yet-persisted (optimistic) row → avoids a 404.
    if (!completionId || String(completionId).startsWith("temp-")) return;
    const before = completions;
    setCompletions((prev) => prev.filter((c) => c.id !== completionId));
    try {
      await api.delete(`/activity-completions/${completionId}`);
    } catch (e) {
      setCompletions(before);
    }
  }

  return (
    <div data-testid="activity-picker" className="space-y-4">
      <ProgressCard accent={accent} count={count} remaining={remaining} pct={pct} stepNum={stepNum} />

      {!compact && (
        <>
          <RecentList completions={recentCompletions} onUndo={undoCompletion} />
          <div className="space-y-2">
            {ACTIVITY_CATEGORIES.map((cat) => (
              <CategorySection
                key={cat.id}
                cat={cat}
                isOpen={openCat === cat.id}
                onToggle={() => setOpenCat(openCat === cat.id ? null : cat.id)}
                perActivityCount={perActivityCount}
                pendingId={pendingId}
                onLog={logActivity}
              />
            ))}
          </div>
        </>
      )}

      <Celebration show={celebrate} accent={accent} stepNum={stepNum} />
    </div>
  );
}
